import React, { useMemo, useRef } from 'react';

import {
  fallbackToScientificNotation,
  getChartDropdownOptions,
  getPipelineChartLink,
  isZeroOrEmptyChartValue,
} from '../../../utils/chart_utils/chart_utils';

import useCSVChartData from '../helpers/useCSVChartData';
import usePrometheusQuery from '../../../utils/hooks/use_prometheus_query';

import { ChartProvider } from '../ChartContext';
import MultiAverageDetailPane from '../details/MultiAverageDetailPane';

import {
  formatSequencedLineChartDatasets,
  getParsedChartPoint,
  getThreeDatasetLineChartDatasets,
} from '../helpers/dataset.helpers';
import { formatChartDate } from '../../../utils/date_utils';

import { ChartWrapper } from '../d3-charts/ChartWrapper';
import LineChart from '../d3-charts/line/D3LineChart';

import { useRefDimensions } from '../d3-charts/d3-helpers';

export const useTTFTData = ({
  deploymentID,
  startTimestamp,
  endTimestamp,
  step
}) => {
  const prometheusRequestObj = {
    startTimestamp,
    endTimestamp,
    step,
    transformData: data => data?.map(point => getParsedChartPoint(point[1], point[0])),
  };

  const ttft99Response = usePrometheusQuery(
    deploymentID === null
      ? null
      : `histogram_quantile(0.99, sum(rate(vllm:time_to_first_token_seconds_bucket{namespace="${deploymentID}"}[${step}s])) by (le)) * 1000`,
    {
      ...prometheusRequestObj,
      queryKey: `ttft-p99-${deploymentID}`,
    },
  );
  const ttft95Response = usePrometheusQuery(
    deploymentID === null
      ? null
      : `histogram_quantile(0.95, sum(rate(vllm:time_to_first_token_seconds_bucket{namespace="${deploymentID}"}[${step}s])) by (le)) * 1000`,
    {
      ...prometheusRequestObj,
      queryKey: `ttft-p95-${deploymentID}`,
    },
  );
  const ttft50Response = usePrometheusQuery(
    deploymentID === null
      ? null
      : `histogram_quantile(0.50, sum(rate(vllm:time_to_first_token_seconds_bucket{namespace="${deploymentID}"}[${step}s])) by (le)) * 1000`,
    {
      ...prometheusRequestObj,
      queryKey: `ttft-p50-${deploymentID}`,
    },
  );

  const p99LatencyData = ttft99Response?.data;
  const p95LatencyData = ttft95Response?.data;
  const p50LatencyData = ttft50Response?.data;

  const transformedDatasets = useMemo(() => getThreeDatasetLineChartDatasets(p99LatencyData, p95LatencyData, p50LatencyData).map(formatSequencedLineChartDatasets), [p99LatencyData, p95LatencyData, p50LatencyData]);

  const csvDatasets = useMemo(() => {
    if (!transformedDatasets?.length) {
      return [];
    }
    return transformedDatasets?.map(dataset => ({
      ...dataset,
      data: dataset?.data?.map(point => isZeroOrEmptyChartValue(point.y) ? { y: 0, x: point.x } : point),
    }))
  }, [transformedDatasets])

  const csvData = useCSVChartData({
    chartType: 'multi',
    title: 'Time to first Token (TTFT)',
    data: csvDatasets,
  })

  return {
    datasets: transformedDatasets,
    csvData,
    isLoading: ttft99Response?.isLoading
      || ttft95Response?.isLoading
      || ttft50Response?.isLoading,
    message: ttft99Response?.error
      || ttft95Response?.error
      || ttft50Response?.error
      || null,
  }
}

export const TTFTChart = ({
  datasets,
  csvData,
  labelFormatString,
  isLoading,
  message,
  pipelineID,
  pipelineVersion,
  chartID,
  startDate,
  endDate,
  isEnlarged = false,
}) => {
  const chartTitle = 'Time to first Token (TTFT)';

  const chartLink = useMemo(() => getPipelineChartLink({
    pipelineID, pipelineVersion, chartID,
  }), [chartID, pipelineID, pipelineVersion]);

  const dropdownOptions = getChartDropdownOptions({
    chartLink,
    chartTitle,
    csvData,
    pipelineID,
    pipelineVersion,
    isEnlarged,
  })

  const containerRef = useRef();
  const dimensions = useRefDimensions(containerRef)

  return (
    <ChartProvider
      chartID={chartID}
      dropdownOptions={dropdownOptions}
      units="ms"
      datasets={datasets}
      usePaddedMax
    >
      <ChartWrapper
        containerRef={containerRef}
        chartTitle={chartTitle}
        isLoading={isLoading}
        message={message}
        detailRenderer={MultiAverageDetailPane}
      >
        <LineChart
          chartId="time-to-first-token"
          datasets={datasets}
          xTickFormat={formatChartDate(labelFormatString)}
          yTickFormat={fallbackToScientificNotation}
          numberOfXTicks={6}
          numberOfYTicks={6}
          startDate={startDate}
          endDate={endDate}
          areaOpacity={0.8}
          containerHeight={dimensions?.height && dimensions.height > 0 ? dimensions.height : 0}
          containerWidth={dimensions?.width && dimensions.width > 0 ? dimensions.width : 0}
          showPoints
        />
      </ChartWrapper>
    </ChartProvider>
  )
}

const TTFTChartWithData = (props) => {
  const {
    deploymentID, startTimestamp, endTimestamp, step,
  } = props;

  const {
    datasets,
    csvData,
    isLoading,
    message,
  } = useTTFTData({
    deploymentID, startTimestamp, endTimestamp, step
  })

  return (
    <TTFTChart
      {...props}
      datasets={datasets}
      csvData={csvData}
      isLoading={isLoading}
      message={message}
      startDate={new Date(startTimestamp)}
      endDate={new Date(endTimestamp)}
    />
  )
}

export default TTFTChartWithData;
